import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { devicesAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Terminal,
  Plus,
  Play,
  Trash,
  CircleNotch,
  CheckCircle,
  XCircle,
  Clock,
  CaretDown,
  CaretRight,
  Desktop,
  FolderSimple,
  Buildings,
  Lightning,
  ArrowClockwise,
  HardDrives,
  Info,
  ShieldCheck,
  Wrench,
  WifiHigh,
  Broom,
  FileText,
  WarningCircle,
  Funnel,
  ArrowsClockwise
} from '@phosphor-icons/react';

const statusColors = {
  pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  running: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  completed: 'bg-green-500/10 text-green-400 border-green-500/20',
  partial: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  failed: 'bg-red-500/10 text-red-400 border-red-500/20',
  timeout: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  cancelled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

const statusLabels = {
  pending: 'Ausstehend',
  running: 'Laeuft',
  completed: 'Erfolgreich',
  partial: 'Teilweise',
  failed: 'Fehlgeschlagen',
  timeout: 'Timeout',
  cancelled: 'Abgebrochen',
};

const StatusIcon = ({ status, size = 16 }) => {
  if (status === 'completed' || status === 'success') return <CheckCircle size={size} className="text-green-400" />;
  if (status === 'failed' || status === 'error') return <XCircle size={size} className="text-red-400" />;
  if (status === 'partial') return <WarningCircle size={size} className="text-orange-400" />;
  if (status === 'running') return <CircleNotch size={size} className="animate-spin text-blue-400" />;
  if (status === 'timeout') return <WarningCircle size={size} className="text-orange-400" />;
  if (status === 'cancelled') return <XCircle size={size} className="text-zinc-400" />;
  return <Clock size={size} className="text-yellow-400" />;
};

/* Helper: elapsed time since */
const elapsedSince = (isoDate) => {
  if (!isoDate) return '';
  const ms = Date.now() - new Date(isoDate).getTime();
  if (ms < 0) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const scriptTemplates = [
  { id: 'sysinfo', name: 'Systeminformationen', description: 'Hostname, OS, RAM, CPU, Uptime', icon: Info, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', script_type: 'powershell', script_content: `$os = Get-CimInstance Win32_OperatingSystem\n$cpu = Get-CimInstance Win32_Processor | Select-Object -First 1\n$uptime = (Get-Date) - $os.LastBootUpTime\nWrite-Output "=== Systeminformationen ==="\nWrite-Output "Hostname: $($env:COMPUTERNAME)"\nWrite-Output "OS: $($os.Caption) $($os.Version)"\nWrite-Output "RAM: $([math]::Round($os.TotalVisibleMemorySize/1MB,1)) GB (Frei: $([math]::Round($os.FreePhysicalMemory/1MB,1)) GB)"\nWrite-Output "CPU: $($cpu.Name)"\nWrite-Output "Uptime: $($uptime.Days)d $($uptime.Hours)h $($uptime.Minutes)m"` },
  { id: 'diskspace', name: 'Festplattenplatz', description: 'Speicherplatz aller Laufwerke', icon: HardDrives, color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', script_type: 'powershell', script_content: `Get-CimInstance Win32_LogicalDisk -Filter "DriveType=3" | ForEach-Object {\n  $total = [math]::Round($_.Size/1GB,1)\n  $free = [math]::Round($_.FreeSpace/1GB,1)\n  $used = $total - $free\n  $pct = [math]::Round(($used/$total)*100,0)\n  Write-Output "$($_.DeviceID) $used/$total GB belegt ($pct%)"\n}` },
  { id: 'winupdate', name: 'Windows Update', description: 'Updates pruefen & installieren', icon: ArrowClockwise, color: 'text-green-400 bg-green-500/10 border-green-500/20', script_type: 'powershell', script_content: `Write-Output "Pruefe Windows Updates..."\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartScan" -Wait\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartDownload" -Wait\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartInstall" -Wait\nWrite-Output "Windows Update gestartet."` },
  { id: 'diskcleaner', name: 'Disk Cleanup', description: 'Temp-Dateien & Cache loeschen', icon: Broom, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', script_type: 'powershell', script_content: `Write-Output "=== Disk Cleanup ==="\n$before = (Get-CimInstance Win32_LogicalDisk -Filter "DeviceID='C:'").FreeSpace\nRemove-Item "$env:TEMP\\*" -Recurse -Force -ErrorAction SilentlyContinue\nRemove-Item "C:\\Windows\\Temp\\*" -Recurse -Force -ErrorAction SilentlyContinue\n$after = (Get-CimInstance Win32_LogicalDisk -Filter "DeviceID='C:'").FreeSpace\n$freed = [math]::Round(($after - $before)/1MB, 1)\nWrite-Output "Bereinigt: $freed MB freigegeben"` },
  { id: 'services', name: 'Dienste-Status', description: 'Wichtige Windows-Dienste', icon: Wrench, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', script_type: 'powershell', script_content: `$services = @("WinRM","Spooler","W32Time","wuauserv","BITS","EventLog","Dnscache")\nWrite-Output "=== Dienste-Status ==="\nforeach ($svc in $services) {\n  $s = Get-Service -Name $svc -ErrorAction SilentlyContinue\n  if ($s) { Write-Output "$($s.DisplayName): $($s.Status)" }\n  else { Write-Output "$svc: NICHT GEFUNDEN" }\n}` },
  { id: 'network', name: 'Netzwerk-Info', description: 'IP, DNS und Gateway', icon: WifiHigh, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20', script_type: 'powershell', script_content: `Write-Output "=== Netzwerk ==="\nGet-NetIPAddress -AddressFamily IPv4 | Where-Object { $_.InterfaceAlias -notlike "Loopback*" } | ForEach-Object {\n  Write-Output "$($_.InterfaceAlias): $($_.IPAddress)/$($_.PrefixLength)"\n}` },
  { id: 'restart', name: 'PC Neustarten', description: 'Neustart in 10 Sekunden', icon: ArrowClockwise, color: 'text-red-400 bg-red-500/10 border-red-500/20', script_type: 'powershell', script_content: `Write-Output "Neustart in 10 Sekunden..."\nshutdown /r /t 10 /c "TSR Remote Neustart"` },
  { id: 'security', name: 'Sicherheits-Check', description: 'Firewall, Defender, Ports', icon: ShieldCheck, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', script_type: 'powershell', script_content: `Write-Output "=== Sicherheits-Check ==="\n$fw = Get-NetFirewallProfile | ForEach-Object { "$($_.Name): $($_.Enabled)" }\nWrite-Output "Firewall: $($fw -join ', ')"` },
  { id: 'eventlog', name: 'Fehler-Log', description: 'Letzte 20 Systemfehler', icon: FileText, color: 'text-pink-400 bg-pink-500/10 border-pink-500/20', script_type: 'powershell', script_content: `Write-Output "=== Letzte Fehler ==="\nGet-EventLog -LogName System -EntryType Error -Newest 20 | ForEach-Object {\n  Write-Output "[$($_.TimeGenerated.ToString('dd.MM.yyyy HH:mm'))] $($_.Source): $($_.Message.Substring(0, [Math]::Min(120, $_.Message.Length)))..."\n}` },
];

export default function ScriptsPage() {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('log');
  const [resultFilter, setResultFilter] = useState('all');
  const [templateTargetOpen, setTemplateTargetOpen] = useState(null);
  const [templateTargetType, setTemplateTargetType] = useState('tenant');
  const [templateTargetIds, setTemplateTargetIds] = useState([]);
  const [form, setForm] = useState({
    name: '', script_content: '', script_type: 'powershell',
    target_type: 'tenant', target_ids: [], run_as_admin: true, silent: true,
  });

  const fetchTasks = useCallback(async () => {
    try {
      const res = await api.get('/scripts');
      setTasks(res.data.tasks);
    } catch (e) {
      toast.error('Skript-Tasks konnten nicht geladen werden');
    } finally { setLoading(false); }
  }, []);

  const fetchGroups = async () => {
    try { const res = await api.get('/groups'); setGroups(res.data.groups); } catch (e) { console.error(e); }
  };

  const fetchDevices = async () => {
    try { const res = await devicesAPI.getAll({ per_page: 500 }); setDevices(res.data.devices); } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchTasks(); fetchGroups(); fetchDevices(); }, [fetchTasks]);

  useEffect(() => {
    const hasRunning = tasks.some(t => t.status === 'pending' || t.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(fetchTasks, 2000);
    return () => clearInterval(interval);
  }, [tasks, fetchTasks]);

  const openCreate = () => {
    setForm({ name: '', script_content: '', script_type: 'powershell', target_type: 'tenant', target_ids: [], run_as_admin: true, silent: true });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name erforderlich'); return; }
    if (!form.script_content.trim()) { toast.error('Skript-Inhalt erforderlich'); return; }
    if (form.target_type !== 'tenant' && form.target_ids.length === 0) { toast.error('Bitte Ziel auswaehlen'); return; }
    setSaving(true);
    try {
      await api.post('/scripts', form);
      toast.success('Skript-Task erstellt');
      setCreateOpen(false);
      fetchTasks();
    } catch (e) { toast.error('Fehler beim Erstellen'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (taskId) => {
    try { await api.delete(`/scripts/${taskId}`); toast.success('Task geloescht'); fetchTasks(); } catch (e) { toast.error('Fehler'); }
  };

  const toggleTarget = (id) => {
    setForm(prev => ({ ...prev, target_ids: prev.target_ids.includes(id) ? prev.target_ids.filter(i => i !== id) : [...prev.target_ids, id] }));
  };
  const toggleTemplateTarget = (id) => {
    setTemplateTargetIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const getDeviceName = (deviceId) => {
    const d = devices.find(dev => dev.id === deviceId);
    return d ? d.computer_name : deviceId.substring(0, 8) + '...';
  };

  const executeTemplate = async (template) => {
    if (templateTargetType !== 'tenant' && templateTargetIds.length === 0) {
      toast.error('Bitte mindestens ein Ziel auswaehlen'); return;
    }
    setSaving(true);
    try {
      await api.post('/scripts', {
        name: template.name, script_content: template.script_content, script_type: template.script_type,
        target_type: templateTargetType, target_ids: templateTargetType === 'tenant' ? [] : templateTargetIds,
        run_as_admin: true, silent: true,
      });
      toast.success(`"${template.name}" wird ausgefuehrt`);
      setTemplateTargetOpen(null); setTemplateTargetIds([]);
      setActiveTab('log');
      fetchTasks();
    } catch (e) { toast.error('Fehler'); }
    finally { setSaving(false); }
  };

  // ========== HELPERS ==========
  const getTaskStats = (task) => {
    const results = task.results || [];
    const total = task.target_device_ids?.length || 0;
    const reported = results.length;
    const success = results.filter(r => r.status === 'success' || r.status === 'completed').length;
    const failed = results.filter(r => r.status === 'failed' || r.status === 'error').length;
    const timeout = results.filter(r => r.status === 'timeout').length;
    const pending = total - reported;
    return { total, reported, success, failed, timeout, pending };
  };

  const getFilteredResults = (task) => {
    const results = task.results || [];
    const allDeviceIds = task.target_device_ids || [];
    const dinfo = task.dispatch_info || {};
    
    // Build complete list including pending devices with dispatch info
    const fullList = allDeviceIds.map(did => {
      const result = results.find(r => r.device_id === did);
      const di = dinfo[did] || {};
      const base = {
        device_id: did,
        device_name: di.computer_name || getDeviceName(did),
        agent_type: di.agent_type,
        agent_version: di.agent_version,
        dispatch_method: di.dispatch_method,
        dispatched_at: di.dispatched_at,
        dispatch_error: di.dispatch_error,
        is_online: di.is_online,
      };
      return result ? { ...base, ...result, device_name: base.device_name } : { ...base, status: 'pending', output: null, error: null, exit_code: null, reported_at: null };
    });

    if (resultFilter === 'success') return fullList.filter(r => r.status === 'success' || r.status === 'completed');
    if (resultFilter === 'failed') return fullList.filter(r => r.status === 'failed' || r.status === 'error' || r.status === 'timeout');
    if (resultFilter === 'pending') return fullList.filter(r => r.status === 'pending');
    return fullList;
  };

  const handleRetry = async (taskId, deviceId) => {
    try {
      const res = await api.post(`/scripts/${taskId}/retry/${deviceId}`);
      toast.success(res.data.dispatched ? 'Erneut gesendet via WebSocket' : 'Wartet auf Agent-Polling');
      fetchTasks();
    } catch (e) {
      toast.error('Retry fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    }
  };

  const formatDuration = (createdAt, updatedAt) => {
    if (!createdAt || !updatedAt) return '';
    const ms = new Date(updatedAt) - new Date(createdAt);
    if (ms < 1000) return '<1s';
    if (ms < 60000) return `${Math.round(ms/1000)}s`;
    return `${Math.floor(ms/60000)}m ${Math.round((ms%60000)/1000)}s`;
  };

  // ========== TARGET SELECTOR ==========
  const TargetSelector = ({ targetType, setTargetType, targetIds, toggleId }) => (
    <div>
      <Label className="text-[#a1a1aa] text-sm">Ziel</Label>
      <div className="flex flex-wrap gap-2 mt-1.5 mb-3">
        {['device', 'group', 'tenant'].map(t => (
          <Button key={t} variant={targetType === t ? 'default' : 'outline'}
            onClick={() => { setTargetType(t); }}
            className={targetType === t ? 'bg-[#D50C2D]' : 'border-white/10'} size="sm">
            {t === 'device' && <><Desktop size={14} /> Geraete</>}
            {t === 'group' && <><FolderSimple size={14} /> Gruppen</>}
            {t === 'tenant' && <><Buildings size={14} /> Alle Geraete</>}
          </Button>
        ))}
      </div>
      {targetType === 'tenant' ? (
        <div className="p-3 bg-[#0A0A0B] border border-white/10 rounded text-center">
          <Buildings size={24} className="mx-auto text-[#D50C2D] mb-1" />
          <p className="text-white text-sm font-medium">Kompletter Mandant</p>
          <p className="text-[#52525b] text-xs">{devices.length} Geraete</p>
        </div>
      ) : (
        <div className="max-h-40 overflow-y-auto bg-[#0A0A0B] border border-white/10 rounded">
          {(targetType === 'device' ? devices : groups).map(item => (
            <div key={item.id} onClick={() => toggleId(item.id)}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${targetIds.includes(item.id) ? 'bg-[#D50C2D]/10 text-white' : 'text-[#a1a1aa] hover:bg-white/5'}`}>
              <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${targetIds.includes(item.id) ? 'bg-[#D50C2D] border-[#D50C2D]' : 'border-white/20'}`}>
                {targetIds.includes(item.id) && <CheckCircle size={10} className="text-white" />}
              </div>
              {targetType === 'device' ? <Desktop size={14} className="text-[#52525b] shrink-0" /> : <FolderSimple size={14} className="text-[#52525b] shrink-0" />}
              <span className="text-sm truncate">{item.computer_name || item.name}</span>
              <span className="ml-auto text-xs shrink-0 text-[#52525b]">
                {targetType === 'device' ? (item.is_online ? 'Online' : 'Offline') : `${item.device_count} Geraete`}
              </span>
            </div>
          ))}
          {(targetType === 'device' ? devices : groups).length === 0 && <p className="text-[#52525b] text-xs p-3 text-center">Keine vorhanden</p>}
        </div>
      )}
      {targetType !== 'tenant' && <p className="text-[#52525b] text-xs mt-1">{targetIds.length} ausgewaehlt</p>}
    </div>
  );

  return (
    <div data-testid="scripts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Remote-Skripte</h1>
          <p className="text-[#a1a1aa] text-sm mt-1">{tasks.length} Task{tasks.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchTasks} className="border-white/10" data-testid="refresh-tasks-btn">
            <ArrowsClockwise size={14} />
          </Button>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-[#D50C2D] hover:bg-[#f21d41]" data-testid="create-script-btn">
              <Plus size={16} /><span>Eigenes Skript</span>
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#141416] border border-white/10 rounded-lg p-1 w-fit">
        <button onClick={() => setActiveTab('log')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'log' ? 'bg-[#D50C2D] text-white' : 'text-[#a1a1aa] hover:text-white hover:bg-white/5'}`}
          data-testid="tab-log">
          <Terminal size={14} className="inline mr-1.5 -mt-0.5" />
          Protokoll ({tasks.length})
        </button>
        <button onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'templates' ? 'bg-[#D50C2D] text-white' : 'text-[#a1a1aa] hover:text-white hover:bg-white/5'}`}
          data-testid="tab-templates">
          <Lightning size={14} className="inline mr-1.5 -mt-0.5" />
          Vorlagen
        </button>
      </div>

      {/* ========== LOG/PROTOCOL TAB ========== */}
      {activeTab === 'log' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-16 bg-[#141416] border border-white/10 rounded">
              <Terminal size={48} className="mx-auto text-[#52525b] mb-4" />
              <p className="text-[#a1a1aa]">Noch keine Skripte ausgefuehrt</p>
              <p className="text-[#52525b] text-sm mt-1">Starte Skripte ueber die Geraete-Seite oder die Vorlagen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map(task => {
                const stats = getTaskStats(task);
                const isExpanded = expandedTask === task.id;
                const filteredResults = isExpanded ? getFilteredResults(task) : [];

                return (
                  <div key={task.id} className="bg-[#141416] border border-white/10 rounded overflow-hidden" data-testid={`script-task-${task.id}`}>
                    {/* Task Header */}
                    <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors"
                      onClick={() => { setExpandedTask(isExpanded ? null : task.id); setResultFilter('all'); }}>
                      {isExpanded ? <CaretDown size={14} className="text-[#52525b] shrink-0" /> : <CaretRight size={14} className="text-[#52525b] shrink-0" />}
                      <StatusIcon status={task.status} />
                      <div className="flex-1 min-w-0">
                        <span className="text-white font-medium text-sm block truncate">{task.name}</span>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-[#52525b] text-xs">{task.script_type?.toUpperCase()}</span>
                          <span className="text-[#52525b] text-xs">
                            {task.target_type === 'tenant' ? 'Alle Geraete' : task.target_type === 'group' ? 'Gruppe(n)' : 'Geraet(e)'}
                          </span>
                          <span className="text-[#52525b] text-xs">von {task.created_by}</span>
                        </div>
                      </div>

                      {/* Stats badges */}
                      <div className="flex items-center gap-2 shrink-0">
                        {stats.success > 0 && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20">
                            <CheckCircle size={12} /> {stats.success}
                          </span>
                        )}
                        {stats.failed > 0 && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
                            <XCircle size={12} /> {stats.failed}
                          </span>
                        )}
                        {stats.pending > 0 && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                            <Clock size={12} /> {stats.pending}
                          </span>
                        )}
                      </div>

                      <span className={`text-xs px-2 py-0.5 rounded border hidden sm:inline shrink-0 ${statusColors[task.status] || statusColors.pending}`}>
                        {statusLabels[task.status] || task.status}
                      </span>
                      <span className="text-[#52525b] text-xs hidden sm:inline shrink-0">{new Date(task.created_at).toLocaleString('de-DE')}</span>
                      {isAdmin && (
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(task.id); }}
                          className="p-1.5 text-[#52525b] hover:text-red-400 hover:bg-red-500/10 rounded shrink-0" data-testid={`delete-task-${task.id}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </div>

                    {/* ========== EXPANDED LOG DETAILS ========== */}
                    {isExpanded && (
                      <div className="border-t border-white/5">
                        {/* Summary Bar */}
                        <div className="px-4 py-3 bg-[#0A0A0B]/50 flex flex-wrap items-center gap-4 text-xs">
                          <div className="flex items-center gap-4">
                            <span className="text-[#52525b]">Gesamt: <span className="text-white font-medium">{stats.total}</span></span>
                            <span className="text-green-400">{stats.success} erfolgreich</span>
                            {stats.failed > 0 && <span className="text-red-400 font-medium">{stats.failed} fehlgeschlagen</span>}
                            {stats.timeout > 0 && <span className="text-orange-400">{stats.timeout} timeout</span>}
                            {stats.pending > 0 && <span className="text-yellow-400">{stats.pending} ausstehend</span>}
                          </div>
                          {task.updated_at && task.created_at && task.status !== 'pending' && (
                            <span className="text-[#52525b]">Dauer: {formatDuration(task.created_at, task.updated_at)}</span>
                          )}
                          <span className="text-[#52525b] sm:hidden">{new Date(task.created_at).toLocaleString('de-DE')}</span>
                        </div>

                        {/* Result Filter */}
                        {stats.total > 1 && (
                          <div className="px-4 py-2 border-t border-white/5 flex items-center gap-2">
                            <Funnel size={12} className="text-[#52525b]" />
                            {['all', 'success', 'failed', 'pending'].map(f => (
                              <button key={f} onClick={() => setResultFilter(f)}
                                className={`text-xs px-2 py-1 rounded transition-colors ${resultFilter === f ? 'bg-white/10 text-white' : 'text-[#52525b] hover:text-[#a1a1aa]'}`}>
                                {f === 'all' && `Alle (${stats.total})`}
                                {f === 'success' && `Erfolgreich (${stats.success})`}
                                {f === 'failed' && `Fehlgeschlagen (${stats.failed})`}
                                {f === 'pending' && `Ausstehend (${stats.pending})`}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Script Content (collapsed) */}
                        <details className="px-4 py-2 border-t border-white/5">
                          <summary className="text-[#52525b] text-xs cursor-pointer hover:text-[#a1a1aa]">Skript anzeigen</summary>
                          <pre className="mt-2 p-3 bg-[#0A0A0B] rounded text-xs text-[#a1a1aa] font-mono overflow-x-auto max-h-48 whitespace-pre-wrap">{task.script_content}</pre>
                        </details>

                        {/* Per-Device Results */}
                        <div className="px-4 py-3 border-t border-white/5">
                          <div className="space-y-2">
                            {filteredResults.length === 0 && (
                              <p className="text-[#52525b] text-xs py-2 text-center">Keine Ergebnisse fuer diesen Filter</p>
                            )}
                            {filteredResults.map((r, i) => (
                              <div key={i} className={`rounded border overflow-hidden ${
                                r.status === 'success' || r.status === 'completed' ? 'bg-green-500/5 border-green-500/10' :
                                r.status === 'failed' || r.status === 'error' ? 'bg-red-500/5 border-red-500/10' :
                                'bg-[#0A0A0B] border-white/5'
                              }`}>
                                {/* Device Result Header */}
                                <div className="flex items-center gap-2 px-3 py-2">
                                  <StatusIcon status={r.status} size={14} />
                                  <Desktop size={12} className="text-[#52525b]" />
                                  <span className="text-white text-xs font-medium">{r.device_name}</span>
                                  {r.agent_type && (
                                    <span className={`text-[9px] px-1 py-0.5 rounded font-mono ${r.agent_type === 'node' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'}`}>
                                      {r.agent_type === 'node' ? 'v2' : 'v1'}
                                    </span>
                                  )}
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    r.status === 'success' || r.status === 'completed' ? 'bg-green-500/10 text-green-400' :
                                    r.status === 'failed' || r.status === 'error' ? 'bg-red-500/10 text-red-400' :
                                    r.status === 'timeout' ? 'bg-orange-500/10 text-orange-400' :
                                    'bg-yellow-500/10 text-yellow-400'
                                  }`}>
                                    {r.status === 'success' || r.status === 'completed' ? 'Erfolgreich' :
                                     r.status === 'failed' || r.status === 'error' ? 'Fehlgeschlagen' :
                                     r.status === 'timeout' ? 'Timeout' : 'Ausstehend'}
                                  </span>
                                  {r.dispatch_method && (
                                    <span className="text-[9px] text-[#3f3f46] font-mono">
                                      via {r.dispatch_method === 'websocket' ? 'WS' : r.dispatch_method === 'fm_channel' ? 'FM' : r.dispatch_method === 'rest_poll' ? 'REST' : 'Queue'}
                                    </span>
                                  )}
                                  {r.exit_code !== null && r.exit_code !== undefined && (
                                    <span className={`text-xs font-mono ${r.exit_code === 0 ? 'text-green-400/60' : 'text-red-400/60'}`}>
                                      Exit: {r.exit_code}
                                    </span>
                                  )}
                                  {r.reported_at && (
                                    <span className="text-[#52525b] text-xs ml-auto">{new Date(r.reported_at).toLocaleString('de-DE')}</span>
                                  )}
                                  {(r.status === 'pending' || r.status === 'timeout' || r.status === 'failed' || r.status === 'error') && isAdmin && (
                                    <button onClick={() => handleRetry(task.id, r.device_id)}
                                      className="ml-auto p-1 rounded text-[#D50C2D] hover:bg-[#D50C2D]/10 transition-colors" title="Erneut senden">
                                      <ArrowsClockwise size={14} />
                                    </button>
                                  )}
                                </div>
                                {/* Output */}
                                {r.output && (
                                  <div className="px-3 pb-2">
                                    <pre className="text-xs text-green-400/80 font-mono whitespace-pre-wrap break-all bg-black/20 rounded p-2">{r.output}</pre>
                                  </div>
                                )}
                                {/* Error */}
                                {r.error && (
                                  <div className="px-3 pb-2">
                                    <div className="bg-red-500/5 border border-red-500/10 rounded p-2">
                                      <span className="text-red-400 text-xs font-medium block mb-1">Fehler:</span>
                                      <pre className="text-xs text-red-400/80 font-mono whitespace-pre-wrap break-all">{r.error}</pre>
                                    </div>
                                  </div>
                                )}
                                {/* Pending state - detailed */}
                                {r.status === 'pending' && (
                                  <div className="px-3 pb-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <CircleNotch size={10} className="animate-spin text-yellow-400" />
                                      <span className="text-yellow-400/80 text-xs">
                                        {r.dispatch_method === 'websocket' ? 'Gesendet via WebSocket - warte auf Antwort...' :
                                         r.dispatch_method === 'fm_channel' ? 'Gesendet via FM-Kanal - Agent fuehrt aus...' :
                                         r.dispatch_method === 'rest_poll' ? 'Agent pollt per REST (~30s Intervall) - warte auf Ausfuehrung...' :
                                         r.dispatch_method === 'offline_queue' ? 'Geraet offline - Task wartet in Queue...' :
                                         'Warte auf Agent...'}
                                      </span>
                                      {task.created_at && <span className="text-[#3f3f46] text-[10px] ml-auto">seit {elapsedSince(task.created_at)}</span>}
                                    </div>
                                    {r.dispatch_error && (
                                      <p className="text-[#52525b] text-[10px] pl-4">{r.dispatch_error}</p>
                                    )}
                                    {!r.agent_type && (
                                      <p className="text-orange-400/60 text-[10px] pl-4">Kein Agent-Typ erkannt (v1-Agent?) - v1-Agents unterstuetzen evtl. keine Script-Ausfuehrung</p>
                                    )}
                                  </div>
                                )}
                                {/* Timeout state */}
                                {r.status === 'timeout' && (
                                  <div className="px-3 pb-2">
                                    <div className="bg-orange-500/5 border border-orange-500/10 rounded p-2">
                                      <span className="text-orange-400 text-xs font-medium block mb-1">Timeout (10 Min)</span>
                                      <pre className="text-xs text-orange-400/70 font-mono whitespace-pre-wrap">{r.error || 'Agent hat nicht geantwortet'}</pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ========== TEMPLATES TAB ========== */}
      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="templates-grid">
          {scriptTemplates.map(tpl => (
            <div key={tpl.id} className="bg-[#141416] border border-white/10 rounded-lg overflow-hidden hover:border-white/20 transition-colors" data-testid={`template-${tpl.id}`}>
              <div className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`p-2 rounded-lg border ${tpl.color}`}><tpl.icon size={20} /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-sm">{tpl.name}</h3>
                    <p className="text-[#52525b] text-xs mt-0.5">{tpl.description}</p>
                  </div>
                </div>
                {templateTargetOpen === tpl.id ? (
                  <div className="space-y-3">
                    <TargetSelector targetType={templateTargetType}
                      setTargetType={(t) => { setTemplateTargetType(t); setTemplateTargetIds([]); }}
                      targetIds={templateTargetIds} toggleId={toggleTemplateTarget} />
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setTemplateTargetOpen(null); setTemplateTargetIds([]); }} className="border-white/10 flex-1">Abbrechen</Button>
                      <Button size="sm" onClick={() => executeTemplate(tpl)} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41] flex-1" data-testid={`execute-template-${tpl.id}`}>
                        {saving ? <CircleNotch size={14} className="animate-spin" /> : <><Play size={14} /> Ausfuehren</>}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => { setTemplateTargetOpen(tpl.id); setTemplateTargetType('tenant'); setTemplateTargetIds([]); }}
                    className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10" data-testid={`open-template-${tpl.id}`}>
                    <Play size={14} /> Ausfuehren
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ========== CREATE CUSTOM SCRIPT DIALOG ========== */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Eigenes Remote-Skript</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-[#a1a1aa]">Name</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" placeholder="z.B. Windows Update" /></div>
              <div><Label className="text-[#a1a1aa]">Typ</Label>
                <Select value={form.script_type} onValueChange={v => setForm(p => ({...p, script_type: v}))}>
                  <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141416] border-white/10"><SelectItem value="powershell">PowerShell</SelectItem><SelectItem value="cmd">CMD</SelectItem><SelectItem value="batch">Batch</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-[#a1a1aa]">Skript-Inhalt</Label>
              <textarea value={form.script_content} onChange={e => setForm(p => ({...p, script_content: e.target.value}))}
                className="w-full mt-1 p-3 bg-[#0A0A0B] border border-white/10 rounded text-sm text-white font-mono resize-y min-h-[120px] focus:outline-none focus:border-[#D50C2D]"
                placeholder="Get-Process | Select-Object Name, CPU" data-testid="script-content-input" />
            </div>
            <TargetSelector targetType={form.target_type}
              setTargetType={(t) => setForm(p => ({...p, target_type: t, target_ids: []}))}
              targetIds={form.target_ids} toggleId={toggleTarget} />
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.run_as_admin} onChange={e => setForm(p => ({...p, run_as_admin: e.target.checked}))} className="accent-[#D50C2D]" />
                <span className="text-sm text-[#a1a1aa]">Als Admin</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.silent} onChange={e => setForm(p => ({...p, silent: e.target.checked}))} className="accent-[#D50C2D]" />
                <span className="text-sm text-[#a1a1aa]">Silent</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-white/10">Abbrechen</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <><Play size={16} /> Ausfuehren</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
